import { navigate } from "@reach/router"
import { ScreenUI } from "@shared/application-components"
import { identifyLDUser } from "@shared/integration/launchdarkly"
import { Badge, Navigation } from "@shared/primitive-components"
import { useLDClient } from "launchdarkly-react-client-sdk"
import React, { useCallback, useMemo, useState } from "react"
import { useAuth } from "../../../contexts"
import { useDatadogRum, useDemoModeFromQueryString } from "../../../hooks"
import { useViewer } from "../../auth/api/useViewer"
import { AdminCarrierClaimsLinks, AdminCarrierLinks, BaseLinks, ScreenGroup, ScreenLink, ScreenLinks } from "./Links"

export const createNavigationLinks = (
  links: ScreenLinks,
  pathname: string,
  setMobileNavIsOpen: (isOpen: boolean) => void,
  setNavTriggered: (triggered: boolean) => void
) => {
  const createLink = (screenLink: ScreenLink) => (
    <Navigation.Link
      key={screenLink.link}
      isDefault={screenLink.isDefault}
      title={screenLink.title}
      value={screenLink.link}
      heading
      pathname={pathname}
      closeMenu={() => {
        setMobileNavIsOpen(false)
        setTimeout(() => window.scrollTo(0, 0), 50)
      }}
      setNavTriggered={setNavTriggered}
      style={{ display: `flex`, justifyContent: `space-between`, alignItems: `center` }}
    >
      {screenLink.title}
      {screenLink.badge && <Badge variant="brand">Beta</Badge>}
    </Navigation.Link>
  )

  return links.map((link) => {
    const { group } = link as ScreenGroup
    if (group) {
      return (
        <Navigation.Group key={link.title} name={link.title} defaultOpen>
          {group.map(createLink)}
        </Navigation.Group>
      )
    }

    return createLink(link as ScreenLink)
  })
}
interface ScreenProps {
  children: React.ReactNode
}

const getLinksForUserType = (type?: string) => {
  if (type === `admin` || type === `carrier`) return AdminCarrierLinks
  if (type === `claims_admin`) return AdminCarrierClaimsLinks
  return BaseLinks
}

export const Screen = ({ children }: ScreenProps) => {
  const [mobileNavIsOpen, setMobileNavIsOpen] = useState(false)
  const [navTriggered, setNavTriggered] = useState(false)
  const homeButtonHandler = useCallback(() => navigate(`/`), [])
  const logoutButtonHandler = useCallback(() => navigate(`/logout`), [])
  const { pathname } = window.location
  const authService = useAuth()

  const user = useMemo(() => authService.getAuthUser(), [authService])
  const links = useMemo(() => getLinksForUserType(user?.type), [user])

  const navigationLinks = useMemo(
    () => createNavigationLinks(links, pathname, setMobileNavIsOpen, setNavTriggered),
    [pathname, links]
  )
  const [res] = useViewer({})
  const appViewer = useMemo(() => {
    if (res.result?.data === undefined) return undefined
    return {
      key: res.result.data.id as string,
      email: res.result.data.email,
      name: res.result.data.name || res.result.data.email,
      userType: res.result.data.type,
    }
  }, [res.result])
  const featureFlagClient = useLDClient()

  useDemoModeFromQueryString(res.result?.data?.id)

  if (appViewer !== undefined && featureFlagClient !== undefined) {
    identifyLDUser(featureFlagClient, appViewer)
  }

  useDatadogRum({ viewer: appViewer })

  return (
    <ScreenUI
      homeButtonHandler={homeButtonHandler}
      logoutButtonHandler={logoutButtonHandler}
      navigationLinks={navigationLinks}
      navigate={navigate}
      showHelpButton
      mobileNavIsOpen={mobileNavIsOpen}
      navTriggered={navTriggered}
      setMobileNavIsOpen={setMobileNavIsOpen}
      setNavTriggered={setNavTriggered}
      hideNavigation={authService.isEmbedded}
    >
      {children}
    </ScreenUI>
  )
}

export default Screen
